// Template builder: form panels on the left, live preview on the right.
// Drafts autosave; Publish freezes the version (immutable per spec §7.1).
// Published rows open read-only with a "new draft" escape hatch.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  ChevronRight,
  Download,
  Trash2,
  Upload,
  UploadCloud,
  Copy,
  UserRound,
  Type,
  Droplet,
  Heading2,
  Image as ImageIcon,
  LayoutGrid,
  ListOrdered,
  Building2,
  Contact,
  type LucideIcon,
} from "lucide-react";
import type { FooterPreset, JdContact } from "@agp/shared";
import {
  ALL_PERSONAL_DETAIL_KEYS,
  ALL_SECTION_KEYS,
  DEFAULT_HEADING_HIGHLIGHT,
  DEFAULT_PERSONAL_DETAIL_LABELS,
  DEFAULT_PERSONAL_DETAILS,
  DEFAULT_SECTION_LABELS,
  type PaperSize,
  type PersonalDetailKey,
  type SectionKey,
  type TemplateDefinition,
  type TemplateRecord,
} from "@agp/shared";
import { useApi } from "@/hooks/useApi";
import { useMe } from "@/hooks/useMe";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label, Select, Textarea } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { StringListEditor } from "@/components/review/editors";
import { TemplatePreview } from "@/components/templates/TemplatePreview";
import { JdPreview } from "@/components/templates/JdPreview";
import { relativeTime } from "@/lib/jobs";
import { cn } from "@/lib/utils";

// Section header with its own icon, per the builder mock.
function SectionHeader({ icon: Icon, title }: { icon: LucideIcon; title: string }) {
  return (
    <CardHeader>
      <CardTitle className="inline-flex items-center gap-2.5 text-base">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-tint text-brand-on">
          <Icon size={15} strokeWidth={1.75} aria-hidden />
        </span>
        {title}
      </CardTitle>
    </CardHeader>
  );
}

const WORD_FONTS = [
  "Calibri",
  "Cambria",
  "Georgia",
  "Garamond",
  "Arial",
  "Times New Roman",
  "Verdana",
  "Tahoma",
  "Book Antiqua",
  "Palatino Linotype",
];

// The definition stores twips (Word's unit); the form shows millimetres.
const twipsToMm = (t: number) => Math.round(t / 56.7);
const mmToTwips = (mm: number) => Math.round(mm * 56.7);

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (hex: string) => void;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={`#${value}`}
          onChange={(e) => onChange(e.target.value.slice(1).toUpperCase())}
          className="h-9 w-12 cursor-pointer rounded-md border border-border bg-surface"
          aria-label={label}
        />
        <Input
          value={value}
          className="w-24 font-mono uppercase"
          onChange={(e) => {
            const v = e.target.value.replace(/^#/, "");
            if (/^[0-9a-fA-F]{0,6}$/.test(v)) onChange(v.toUpperCase());
          }}
        />
      </div>
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step,
  suffix,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
}) {
  return (
    <div>
      <Label>
        {label}
        {suffix ? ` (${suffix})` : ""}
      </Label>
      <Input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step ?? 1}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

export function TemplateBuilder() {
  const { rowId = "" } = useParams();
  const api = useApi();
  const me = useMe();
  const navigate = useNavigate();
  const footerPresetsQuery = useQuery({
    queryKey: ["footer-presets"],
    queryFn: () => api.get<{ presets: FooterPreset[] }>("/api/footer-presets"),
  });
  const footerPresets = footerPresetsQuery.data?.presets ?? [];
  const qc = useQueryClient();

  const recordQuery = useQuery({
    queryKey: ["admin-template", rowId],
    queryFn: () => api.get<TemplateRecord>(`/api/admin/templates/${rowId}`),
    enabled: me?.role === "admin",
  });

  const [def, setDef] = useState<TemplateDefinition | null>(null);
  // Less-touched settings live behind one disclosure so the common cards
  // (identity, type, colors, layout) stay above the fold, per the mock.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [assetUrls, setAssetUrls] = useState<{ logo: string | null; watermark: string | null }>({
    logo: null,
    watermark: null,
  });
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [actionError, setActionError] = useState<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestDef = useRef<TemplateDefinition | null>(null);

  const record = recordQuery.data ?? null;
  const readOnly = record?.status === "published";

  useEffect(() => {
    if (record && def === null) {
      setDef(record.definition);
      latestDef.current = record.definition;
      setAssetUrls({ logo: record.logoUrl, watermark: record.watermarkUrl });
    }
  }, [record, def]);

  const persist = useCallback(async () => {
    if (!latestDef.current || readOnly) return;
    setSaveState("saving");
    try {
      await api.patch(`/api/admin/templates/${rowId}`, { definition: latestDef.current });
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  }, [api, rowId, readOnly]);

  const update = useCallback(
    (patch: Partial<TemplateDefinition>) => {
      if (readOnly) return;
      setDef((prev) => {
        if (!prev) return prev;
        const next = { ...prev, ...patch };
        latestDef.current = next;
        return next;
      });
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => void persist(), 900);
    },
    [persist, readOnly],
  );

  // The footer controls each change one field but must send the whole
  // footerStyle object — building it per-control silently dropped any field
  // the control didn't know about, so all of them go through this instead.
  const patchFooterStyle = (
    patch: Partial<NonNullable<TemplateDefinition["branding"]["footerStyle"]>>,
  ) => {
    if (!def) return;
    update({
      branding: {
        ...def.branding,
        footerStyle: {
          // Stays unset until picked so the footer keeps following the body font.
          font: def.branding.footerStyle?.font,
          sizePt: def.branding.footerStyle?.sizePt ?? 8,
          bold: def.branding.footerStyle?.bold === true,
          color: def.branding.footerStyle?.color ?? def.colors.subtle,
          showRule: def.branding.footerStyle?.showRule !== false,
          ruleWidthPt: def.branding.footerStyle?.ruleWidthPt ?? 1,
          ruleSpacingPt: def.branding.footerStyle?.ruleSpacingPt ?? 4,
          ...patch,
        },
      },
    });
  };

  const removeAsset = useCallback(
    (kind: "logo" | "watermark") => {
      // Clears the reference only; the stored S3 object is left alone because
      // an earlier published version copied from this draft may still use it.
      setAssetUrls((prev) => ({ ...prev, [kind]: null }));
      setDef((prev) => {
        if (!prev) return prev;
        const next = {
          ...prev,
          branding: {
            ...prev.branding,
            ...(kind === "logo"
              ? { logoFile: null, logoS3Key: null }
              : { watermarkFile: null, watermarkS3Key: null }),
          },
        };
        latestDef.current = next;
        return next;
      });
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => void persist(), 400);
    },
    [persist],
  );

  const uploadAsset = useCallback(
    async (kind: "logo" | "watermark", file: File) => {
      setActionError(null);
      try {
        const res = await api.postBinary<{
          key: string;
          url: string;
          width: number | null;
          height: number | null;
        }>(
          `/api/admin/templates/${rowId}/asset?kind=${kind}`,
          file,
          file.type || "application/octet-stream",
        );
        setAssetUrls((prev) => ({ ...prev, [kind]: res.url }));
        // Preset the display size from the image's real proportions, scaled
        // into a sensible box — starting from a distorted stretch (square
        // photo forced into a wide logo slot) breaks the image. The user can
        // still adjust afterwards.
        const box = kind === "logo" ? { w: 200, h: 60 } : { w: 420, h: 420 };
        const fitted =
          res.width && res.height
            ? (() => {
                const scale = Math.min(box.w / res.width, box.h / res.height, 1);
                return {
                  width: Math.max(20, Math.round(res.width * scale)),
                  height: Math.max(20, Math.round(res.height * scale)),
                };
              })()
            : null;
        // The server already stored the key on the draft; mirror it locally so
        // later autosaves don't wipe it, and persist the fitted size.
        setDef((prev) => {
          if (!prev) return prev;
          const next = {
            ...prev,
            branding: {
              ...prev.branding,
              [kind === "logo" ? "logoS3Key" : "watermarkS3Key"]: res.key,
              ...(fitted ? { [kind === "logo" ? "logoSize" : "watermarkSize"]: fitted } : {}),
            },
          };
          latestDef.current = next;
          return next;
        });
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => void persist(), 400);
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "Upload failed.");
      }
    },
    [api, rowId, persist],
  );

  const downloadSample = useCallback(async () => {
    setActionError(null);
    try {
      await persist();
      const { blob, filename } = await api.postForBlob(`/api/admin/templates/${rowId}/sample`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename ?? "sample.docx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Sample download failed.");
    }
  }, [api, rowId, persist]);

  // "publish" | "discard" — which ConfirmDialog is open.
  const [confirming, setConfirming] = useState<"publish" | "discard" | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const publish = useCallback(async () => {
    setConfirmBusy(true);
    setActionError(null);
    try {
      await persist();
      await api.post(`/api/admin/templates/${rowId}/publish`);
      // Every open template picker (review screen) and the admin list cache
      // the old version list — drop both so the new version shows without a
      // hard refresh.
      await qc.invalidateQueries({ queryKey: ["templates"] });
      await qc.invalidateQueries({ queryKey: ["admin-templates"] });
      navigate("/templates");
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Publish failed.");
      setConfirming(null);
    } finally {
      setConfirmBusy(false);
    }
  }, [api, rowId, persist, navigate, qc]);

  const discardDraft = useCallback(async () => {
    setConfirmBusy(true);
    try {
      await api.delete(`/api/admin/templates/${rowId}`);
      await qc.invalidateQueries({ queryKey: ["admin-templates"] });
      navigate("/templates");
    } finally {
      setConfirmBusy(false);
    }
  }, [api, rowId, navigate, qc]);

  const newDraftFromThis = useCallback(async () => {
    setActionError(null);
    try {
      const created = await api.post<TemplateRecord>("/api/admin/templates", {
        basedOnTemplateId: record?.templateId,
      });
      setDef(null); // force re-init from the new record
      navigate(`/templates/${created.rowId}`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not create a draft.");
    }
  }, [api, navigate, record?.templateId]);

  const saveLabel = useMemo(() => {
    if (readOnly) return "Published — read-only";
    switch (saveState) {
      case "saving":
        return "Saving…";
      case "saved":
        return "Draft saved";
      case "error":
        return "Save failed — will retry on next change";
      default:
        return "";
    }
  }, [saveState, readOnly]);

  if (me && me.role !== "admin") {
    return (
      <p className="py-10 text-center text-[14px] text-text-3">
        Template administration needs an admin account.
      </p>
    );
  }
  if (!record || !def) {
    return (
      <div className="grid items-start gap-4 pt-2 lg:grid-cols-[minmax(320px,420px)_minmax(0,1fr)]">
        <div className="space-y-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-56 rounded-lg" />
          <Skeleton className="h-72 rounded-lg" />
        </div>
        <Skeleton className="hidden h-[70vh] rounded-lg lg:block" />
      </div>
    );
  }

  // JD templates share branding/typography/page settings but none of the
  // resume-specific structure — those cards are hidden below.
  const isJd = def.kind === "jd";
  // Raw list access while editing (no trimming — a just-added empty row must
  // survive); tolerates the pre-list single-string shape.
  const rawList = (v: string[] | string | null | undefined): string[] =>
    Array.isArray(v) ? v : typeof v === "string" ? [v] : [];
  const patchJdContact = (patch: Partial<JdContact>) =>
    update({
      jdContact: {
        name: def.jdContact?.name ?? null,
        title: def.jdContact?.title ?? null,
        company: def.jdContact?.company ?? null,
        telephone: rawList(def.jdContact?.telephone),
        mobile: rawList(def.jdContact?.mobile),
        email: rawList(def.jdContact?.email),
        website: rawList(def.jdContact?.website),
        ...patch,
      },
    });
  const patchJdContactHeading = (
    patch: Partial<NonNullable<TemplateDefinition["jdContactHeading"]>>,
  ) =>
    update({
      jdContactHeading: {
        text: def.jdContactHeading?.text ?? "Please Contact:",
        sizePt: def.jdContactHeading?.sizePt ?? def.fonts.bodyPt,
        bold: def.jdContactHeading?.bold !== false,
        color: def.jdContactHeading?.color ?? def.colors.text,
        ...patch,
      },
    });
  const patchJdContactStyle = (
    patch: Partial<NonNullable<TemplateDefinition["jdContactStyle"]>>,
  ) =>
    update({
      jdContactStyle: {
        font: def.jdContactStyle?.font ?? def.fonts.body,
        sizePt: def.jdContactStyle?.sizePt ?? def.fonts.bodyPt,
        bold: def.jdContactStyle?.bold === true,
        color: def.jdContactStyle?.color ?? def.colors.text,
        ...patch,
      },
    });
  const unusedSections = ALL_SECTION_KEYS.filter((k) => !def.sections.some((s) => s.key === k));
  // Absent personalDetails means "use the default row set" — show that set so
  // the first edit starts from what the output actually renders.
  const detailRows = def.personalDetails ?? DEFAULT_PERSONAL_DETAILS;
  const unusedDetailKeys = ALL_PERSONAL_DETAIL_KEYS.filter(
    (k) => !detailRows.some((f) => f.key === k),
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Button
            asChild
            variant="secondary"
            size="icon"
            className="h-9 w-9 rounded-full"
            aria-label="Back to templates"
          >
            <Link to="/templates">
              <ArrowLeft size={17} strokeWidth={1.75} />
            </Link>
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="truncate">{def.name}</h3>
              <Badge variant={readOnly ? "ok" : "warn"}>
                v{record.version} · {record.status}
              </Badge>
            </div>
            <p className="truncate text-[12px] text-text-2">
              Updated {relativeTime(record.updatedAt)}
              {saveLabel && ` · ${saveLabel}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => void downloadSample()}>
            <Download size={14} strokeWidth={1.75} /> Sample DOCX
          </Button>
          {readOnly ? (
            <Button size="sm" onClick={() => void newDraftFromThis()}>
              <Copy size={14} strokeWidth={1.75} /> New draft from this
            </Button>
          ) : (
            <>
              <Button variant="secondary" size="sm" onClick={() => setConfirming("discard")}>
                <Trash2 size={14} strokeWidth={1.75} /> Discard
              </Button>
              <Button size="sm" onClick={() => setConfirming("publish")}>
                <UploadCloud size={14} strokeWidth={1.75} /> Publish v{record.version}
              </Button>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirming === "publish"}
        title={`Publish v${record.version}?`}
        body="The version becomes immutable and available to everyone generating resumes. Later changes go into a new draft."
        confirmLabel={`Publish v${record.version}`}
        busy={confirmBusy}
        onConfirm={() => void publish()}
        onCancel={() => setConfirming(null)}
      />
      <ConfirmDialog
        open={confirming === "discard"}
        title="Discard this draft?"
        body="Its changes are lost. Published versions are not affected."
        confirmLabel="Discard draft"
        danger
        busy={confirmBusy}
        onConfirm={() => void discardDraft()}
        onCancel={() => setConfirming(null)}
      />

      {actionError && (
        <p className="rounded-md bg-danger-tint px-4 py-2.5 text-[13px] font-medium text-danger-on">
          {actionError}
        </p>
      )}

      {readOnly && (
        <p className="rounded-md border border-border bg-surface-2 px-4 py-2.5 text-[13px] text-text-2">
          You are viewing <span className="font-semibold text-text">published v{record.version}</span>,
          which is immutable — that keeps every generated document traceable to the exact
          template that produced it. Every field here is read-only; use{" "}
          <span className="font-semibold text-text">&ldquo;New draft from this&rdquo;</span> above
          to make changes, then publish the new version.
        </p>
      )}

      {/* Focus-mode width: the form keeps a fixed readable column and the
          preview absorbs all remaining space. */}
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(380px,560px)_minmax(0,1fr)]">
        <div className="flex flex-col gap-4">
          <Card>
            <SectionHeader icon={UserRound} title="Identity" />
            <CardBody className="flex flex-col gap-3">
              <div>
                <Label>Name</Label>
                <Input
                  value={def.name}
                  disabled={readOnly}
                  onChange={(e) => update({ name: e.target.value })}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  rows={2}
                  value={def.description}
                  disabled={readOnly}
                  onChange={(e) => update({ description: e.target.value })}
                />
              </div>
            </CardBody>
          </Card>

          <Card>
            <SectionHeader icon={Type} title="Typography" />
            <CardBody className="grid grid-cols-2 gap-3">
              <div>
                <Label>Body font</Label>
                <Select
                  value={def.fonts.body}
                  disabled={readOnly}
                  onChange={(e) => update({ fonts: { ...def.fonts, body: e.target.value } })}
                >
                  {WORD_FONTS.map((f) => (
                    <option key={f}>{f}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label>Heading font</Label>
                <Select
                  value={def.fonts.heading}
                  disabled={readOnly}
                  onChange={(e) => update({ fonts: { ...def.fonts, heading: e.target.value } })}
                >
                  {WORD_FONTS.map((f) => (
                    <option key={f}>{f}</option>
                  ))}
                </Select>
              </div>
              <NumberField
                label="Body size"
                suffix="pt"
                min={8}
                max={14}
                step={0.5}
                value={def.fonts.bodyPt}
                onChange={(n) => update({ fonts: { ...def.fonts, bodyPt: n } })}
              />
              <NumberField
                label="Name size"
                suffix="pt"
                min={14}
                max={32}
                step={0.5}
                value={def.fonts.namePt}
                onChange={(n) => update({ fonts: { ...def.fonts, namePt: n } })}
              />
              <NumberField
                label="Heading size"
                suffix="pt"
                min={9}
                max={16}
                step={0.5}
                value={def.fonts.headingPt}
                onChange={(n) => update({ fonts: { ...def.fonts, headingPt: n } })}
              />
              <NumberField
                label="Bullet size"
                suffix="pt"
                min={6}
                max={18}
                step={0.5}
                value={def.fonts.bulletPt ?? def.fonts.bodyPt}
                // Cleared (0) means unset — back to following the body size.
                onChange={(n) =>
                  update({ fonts: { ...def.fonts, bulletPt: n > 0 ? n : undefined } })
                }
              />
              <div>
                <Label>Name case</Label>
                <Select
                  value={def.nameCase ?? "upper"}
                  disabled={readOnly}
                  onChange={(e) =>
                    update({ nameCase: e.target.value as "upper" | "none" })
                  }
                >
                  <option value="upper">UPPERCASE</option>
                  <option value="none">As written</option>
                </Select>
              </div>
            </CardBody>
          </Card>

          <Card>
            <SectionHeader icon={Droplet} title="Colors" />
            <CardBody className="grid grid-cols-3 gap-3">
              <ColorField
                label="Headings"
                value={def.colors.heading}
                onChange={(hex) => update({ colors: { ...def.colors, heading: hex } })}
              />
              <ColorField
                label="Body text"
                value={def.colors.text}
                onChange={(hex) => update({ colors: { ...def.colors, text: hex } })}
              />
              <ColorField
                label="Subtle"
                value={def.colors.subtle}
                onChange={(hex) => update({ colors: { ...def.colors, subtle: hex } })}
              />
            </CardBody>
          </Card>

          <Card>
            <SectionHeader icon={LayoutGrid} title="Layout" />
            <CardBody className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Paper size</Label>
                <Select
                  value={def.page.size ?? "A4"}
                  disabled={readOnly}
                  onChange={(e) =>
                    update({ page: { ...def.page, size: e.target.value as PaperSize } })
                  }
                >
                  <option value="A4">A4 — 210 × 297 mm</option>
                  <option value="Letter">US Letter — 8.5 × 11 in</option>
                </Select>
              </div>
              <NumberField
                label="Top margin"
                suffix="mm"
                min={7}
                max={42}
                value={twipsToMm(def.page.marginTop)}
                onChange={(n) => update({ page: { ...def.page, marginTop: mmToTwips(n) } })}
              />
              <NumberField
                label="Bottom margin"
                suffix="mm"
                min={7}
                max={42}
                value={twipsToMm(def.page.marginBottom)}
                onChange={(n) => update({ page: { ...def.page, marginBottom: mmToTwips(n) } })}
              />
              <NumberField
                label="Left margin"
                suffix="mm"
                min={7}
                max={42}
                value={twipsToMm(def.page.marginLeft)}
                onChange={(n) => update({ page: { ...def.page, marginLeft: mmToTwips(n) } })}
              />
              <NumberField
                label="Right margin"
                suffix="mm"
                min={7}
                max={42}
                value={twipsToMm(def.page.marginRight)}
                onChange={(n) => update({ page: { ...def.page, marginRight: mmToTwips(n) } })}
              />
              <NumberField
                label={isJd ? "Label column" : "Date column"}
                suffix="mm"
                min={21}
                max={56}
                value={twipsToMm(def.dateColumnTwips)}
                onChange={(n) => update({ dateColumnTwips: mmToTwips(n) })}
              />
              {!isJd && (
                <>
                  <div>
                    <Label>Date format</Label>
                    <Select
                      value={def.dateFormat}
                      disabled={readOnly}
                      onChange={(e) =>
                        update({ dateFormat: e.target.value as TemplateDefinition["dateFormat"] })
                      }
                    >
                      <option value="MMM.'yy">Mar.'11</option>
                      <option value="MMM yyyy">Mar 2011</option>
                      <option value="MMMM yyyy">March 2011</option>
                      <option value="MM/yyyy">03/2011</option>
                      <option value="yyyy.MM">2011.03</option>
                      <option value="yyyy年M月">2011年3月</option>
                    </Select>
                  </div>
                  <div>
                    <Label>Languages format</Label>
                    <Select
                      value={def.languageFormat ?? "dash"}
                      disabled={readOnly}
                      onChange={(e) =>
                        update({ languageFormat: e.target.value as "dash" | "paren" })
                      }
                    >
                      <option value="dash">French – Intermediate</option>
                      <option value="paren">French (Intermediate)</option>
                    </Select>
                  </div>
                  <label className="col-span-2 flex items-center gap-2 text-[14px] text-text">
                    <input
                      type="checkbox"
                      checked={def.emphasizeAchievements}
                      disabled={readOnly}
                      onChange={(e) => update({ emphasizeAchievements: e.target.checked })}
                    />
                    Emphasize achievements (compact technical-skill line)
                  </label>
                </>
              )}
            </CardBody>
          </Card>

          {isJd && (
            <Card>
              <SectionHeader icon={Contact} title="Please Contact" />
              <CardBody className="flex flex-col gap-3">
                <div className="grid grid-cols-[1fr_auto_auto_auto] items-end gap-3">
                  <div>
                    <Label>Heading</Label>
                    <Input
                      value={def.jdContactHeading?.text ?? "Please Contact:"}
                      disabled={readOnly}
                      onChange={(e) => patchJdContactHeading({ text: e.target.value })}
                    />
                  </div>
                  <NumberField
                    label="Size"
                    suffix="pt"
                    min={6}
                    max={18}
                    step={0.5}
                    value={def.jdContactHeading?.sizePt ?? def.fonts.bodyPt}
                    onChange={(n) => patchJdContactHeading({ sizePt: n })}
                  />
                  <div>
                    <Label>Color</Label>
                    <input
                      type="color"
                      value={`#${def.jdContactHeading?.color ?? def.colors.text}`}
                      disabled={readOnly}
                      aria-label="Contact heading color"
                      className="h-9 w-12 cursor-pointer rounded-md border border-border bg-surface"
                      onChange={(e) =>
                        patchJdContactHeading({ color: e.target.value.slice(1).toUpperCase() })
                      }
                    />
                  </div>
                  <label className="flex h-9 items-center gap-2 text-[14px] text-text">
                    <input
                      type="checkbox"
                      checked={def.jdContactHeading?.bold !== false}
                      disabled={readOnly}
                      onChange={(e) => patchJdContactHeading({ bold: e.target.checked })}
                    />
                    Bold
                  </label>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={def.jdContact?.name ?? ""}
                      disabled={readOnly}
                      onChange={(e) => patchJdContact({ name: e.target.value || null })}
                    />
                  </div>
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={def.jdContact?.title ?? ""}
                      disabled={readOnly}
                      onChange={(e) => patchJdContact({ title: e.target.value || null })}
                    />
                  </div>
                  <div>
                    <Label>Company</Label>
                    <Input
                      value={def.jdContact?.company ?? ""}
                      disabled={readOnly}
                      onChange={(e) => patchJdContact({ company: e.target.value || null })}
                    />
                  </div>
                </div>
                {(
                  [
                    ["telephone", "Main phone"],
                    ["mobile", "Mobile"],
                    ["email", "Email"],
                    ["website", "Website"],
                  ] as const
                ).map(([key, label]) => (
                  <div key={key}>
                    <Label>{label}</Label>
                    {readOnly ? (
                      <div className="text-[14px] text-text-2">
                        {rawList(def.jdContact?.[key]).filter(Boolean).map((v, i) => (
                          <div key={i}>{v}</div>
                        ))}
                        {rawList(def.jdContact?.[key]).filter(Boolean).length === 0 && <div>—</div>}
                      </div>
                    ) : (
                      <StringListEditor
                        items={rawList(def.jdContact?.[key])}
                        onChange={(values) => patchJdContact({ [key]: values })}
                      />
                    )}
                  </div>
                ))}
                <div className="border-t border-border pt-3">
                  <Label>Contact text styling</Label>
                  <div className="grid grid-cols-[1fr_auto_auto_auto] items-end gap-3">
                    <div>
                      <Label>Font</Label>
                      <Select
                        value={def.jdContactStyle?.font ?? def.fonts.body}
                        disabled={readOnly}
                        onChange={(e) => patchJdContactStyle({ font: e.target.value })}
                      >
                        {WORD_FONTS.map((f) => (
                          <option key={f}>{f}</option>
                        ))}
                      </Select>
                    </div>
                    <NumberField
                      label="Size"
                      suffix="pt"
                      min={6}
                      max={14}
                      step={0.5}
                      value={def.jdContactStyle?.sizePt ?? def.fonts.bodyPt}
                      onChange={(n) => patchJdContactStyle({ sizePt: n })}
                    />
                    <div>
                      <Label>Color</Label>
                      <input
                        type="color"
                        value={`#${def.jdContactStyle?.color ?? def.colors.text}`}
                        disabled={readOnly}
                        aria-label="Contact text color"
                        className="h-9 w-12 cursor-pointer rounded-md border border-border bg-surface"
                        onChange={(e) =>
                          patchJdContactStyle({ color: e.target.value.slice(1).toUpperCase() })
                        }
                      />
                    </div>
                    <label className="flex h-9 items-center gap-2 text-[14px] text-text">
                      <input
                        type="checkbox"
                        checked={def.jdContactStyle?.bold === true}
                        disabled={readOnly}
                        onChange={(e) => patchJdContactStyle({ bold: e.target.checked })}
                      />
                      Bold
                    </label>
                  </div>
                </div>
                <p className="text-[12px] text-text-3">
                  Fixed &ldquo;Please Contact&rdquo; block on every JD generated from this
                  template — each phone, email and website renders as its own line. The heading
                  and the contact lines are styled separately. The review screen&rsquo;s Contact
                  tab can still override the people for a single job; empty fields are skipped
                  in the output.
                </p>
              </CardBody>
            </Card>
          )}

          <button
            type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            aria-expanded={advancedOpen}
            className="flex items-center justify-between rounded-lg border border-border bg-surface px-5 py-3.5 text-left shadow-card transition-colors hover:bg-surface-2"
          >
            <span className="text-[14px] font-semibold text-text">Advanced settings</span>
            <ChevronRight
              size={16}
              strokeWidth={1.75}
              aria-hidden
              className={cn("text-text-2 transition-transform duration-150", advancedOpen && "rotate-90")}
            />
          </button>

          {advancedOpen && (
            <>
          {!isJd && (
            <>
          <Card>
            <SectionHeader icon={Heading2} title="Headings &amp; emphasis" />
            <CardBody className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Sub-header casing</Label>
                  <Select
                    value={def.headingStyle?.case ?? "upper"}
                    disabled={readOnly}
                    onChange={(e) =>
                      update({
                        headingStyle: {
                          case: e.target.value as "upper" | "title" | "none",
                          highlight: def.headingStyle?.highlight ?? null,
                          showRule: def.headingStyle?.showRule !== false,
                        },
                      })
                    }
                  >
                    <option value="upper">UPPERCASE</option>
                    <option value="title">Title Case</option>
                    <option value="none">As typed</option>
                  </Select>
                </div>
                <div>
                  <Label>Highlight</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      // A definition with no headingStyle block renders with
                      // the default grey highlight, so show the box checked.
                      checked={def.headingStyle ? def.headingStyle.highlight != null : true}
                      disabled={readOnly}
                      aria-label="Highlight sub-headers"
                      onChange={(e) =>
                        update({
                          headingStyle: {
                            case: def.headingStyle?.case ?? "upper",
                            highlight: e.target.checked ? DEFAULT_HEADING_HIGHLIGHT : null,
                            showRule: def.headingStyle?.showRule !== false,
                          },
                        })
                      }
                    />
                    {def.headingStyle?.highlight != null && (
                      <input
                        type="color"
                        value={`#${def.headingStyle.highlight}`}
                        disabled={readOnly}
                        aria-label="Highlight color"
                        className="h-9 w-12 cursor-pointer rounded-md border border-border bg-surface"
                        onChange={(e) =>
                          update({
                            headingStyle: {
                              case: def.headingStyle?.case ?? "upper",
                              highlight: e.target.value.slice(1).toUpperCase(),
                              showRule: def.headingStyle?.showRule !== false,
                            },
                          })
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
              <label className="flex items-center gap-2 text-[14px] text-text">
                <input
                  type="checkbox"
                  checked={def.headingStyle?.showRule !== false}
                  disabled={readOnly}
                  onChange={(e) =>
                    update({
                      headingStyle: {
                        case: def.headingStyle?.case ?? "upper",
                        highlight: def.headingStyle?.highlight ?? null,
                        showRule: e.target.checked,
                      },
                    })
                  }
                />
                Underline rule below sub-headers
              </label>
              <div className="border-t border-border pt-3">
                <Label>Current employment (marked “Present”)</Label>
                <label className="flex items-center gap-2 text-[14px] text-text">
                  <input
                    type="checkbox"
                    checked={def.currentEmployment?.underline === true}
                    disabled={readOnly}
                    onChange={(e) =>
                      update({
                        currentEmployment: {
                          underline: e.target.checked,
                          useHeadingColor: def.currentEmployment?.useHeadingColor === true,
                        },
                      })
                    }
                  />
                  Underline the employer line
                </label>
                <label className="mt-1 flex items-center gap-2 text-[14px] text-text">
                  <input
                    type="checkbox"
                    checked={def.currentEmployment?.useHeadingColor === true}
                    disabled={readOnly}
                    onChange={(e) =>
                      update({
                        currentEmployment: {
                          underline: def.currentEmployment?.underline === true,
                          useHeadingColor: e.target.checked,
                        },
                      })
                    }
                  />
                  Show it in the heading color
                </label>
              </div>
              <div className="border-t border-border pt-3">
                <Label>Experience section</Label>
                <label className="flex items-center gap-2 text-[14px] text-text">
                  <input
                    type="checkbox"
                    checked={def.experienceStyle?.employerRule !== false}
                    disabled={readOnly}
                    onChange={(e) =>
                      update({
                        experienceStyle: {
                          titleItalic: def.experienceStyle?.titleItalic === true,
                          employerRule: e.target.checked,
                        },
                      })
                    }
                  />
                  Rule line under the employer row
                </label>
                <label className="mt-1 flex items-center gap-2 text-[14px] text-text">
                  <input
                    type="checkbox"
                    checked={def.experienceStyle?.titleItalic === true}
                    disabled={readOnly}
                    onChange={(e) =>
                      update({
                        experienceStyle: {
                          titleItalic: e.target.checked,
                          employerRule: def.experienceStyle?.employerRule !== false,
                        },
                      })
                    }
                  />
                  Italicize position titles
                </label>
              </div>
              <div className="border-t border-border pt-3">
                <Label>Education section</Label>
                <label className="flex items-center gap-2 text-[14px] text-text">
                  <input
                    type="checkbox"
                    checked={def.educationStyle?.schoolRule === true}
                    disabled={readOnly}
                    onChange={(e) => update({ educationStyle: { schoolRule: e.target.checked } })}
                  />
                  Rule line under each school row
                </label>
              </div>
            </CardBody>
          </Card>

          <Card>
            <SectionHeader icon={ImageIcon} title="Candidate photo" />
            <CardBody className="flex flex-col gap-3">
              <label className="flex items-center gap-2 text-[14px] text-text">
                <input
                  type="checkbox"
                  checked={def.photo?.show === true}
                  disabled={readOnly}
                  onChange={(e) =>
                    update({
                      photo: {
                        show: e.target.checked,
                        position: def.photo?.position ?? "right",
                        widthPx: def.photo?.widthPx ?? 96,
                        heightPx: def.photo?.heightPx ?? 120,
                      },
                    })
                  }
                />
                Show the candidate photo beside the name
              </label>
              {def.photo?.show && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Position</Label>
                    <Select
                      value={def.photo.position}
                      disabled={readOnly}
                      onChange={(e) =>
                        update({
                          photo: {
                            ...def.photo!,
                            position: e.target.value as NonNullable<
                              TemplateDefinition["photo"]
                            >["position"],
                          },
                        })
                      }
                    >
                      <option value="right">Right of name</option>
                      <option value="left">Left of name</option>
                      <option value="above-left">Above name — left</option>
                      <option value="above-center">Above name — center</option>
                      <option value="above-right">Above name — right</option>
                      <option value="below-left">Below name — left</option>
                      <option value="below-center">Below name — center</option>
                      <option value="below-right">Below name — right</option>
                    </Select>
                  </div>
                  <NumberField
                    label="Width"
                    suffix="px"
                    min={40}
                    max={300}
                    value={def.photo.widthPx}
                    onChange={(n) => update({ photo: { ...def.photo!, widthPx: n } })}
                  />
                  <NumberField
                    label="Height"
                    suffix="px"
                    min={40}
                    max={300}
                    value={def.photo.heightPx}
                    onChange={(n) => update({ photo: { ...def.photo!, heightPx: n } })}
                  />
                </div>
              )}
              <p className="text-[12px] text-text-3">
                The photo itself comes from each resume: extracted automatically from Word and
                PDF sources, or uploaded on the review screen. Nothing shows when a job has no
                photo.
              </p>
            </CardBody>
          </Card>

          <Card>
            <SectionHeader icon={ListOrdered} title="Sections" />
            <CardBody className="flex flex-col gap-2">
              {def.sections.map((s, i) => (
                <div key={s.key} className="flex items-center gap-2">
                  <span className="w-32 shrink-0 text-[12px] text-text-3">{s.key}</span>
                  <Input
                    value={s.label}
                    disabled={readOnly || s.key === "otherSections"}
                    onChange={(e) => {
                      const sections = [...def.sections];
                      sections[i] = { ...s, label: e.target.value };
                      update({ sections });
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label="Move up"
                    disabled={readOnly || i === 0}
                    onClick={() => {
                      const sections = [...def.sections];
                      [sections[i - 1], sections[i]] = [sections[i]!, sections[i - 1]!];
                      update({ sections });
                    }}
                  >
                    <ArrowUp size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label="Move down"
                    disabled={readOnly || i === def.sections.length - 1}
                    onClick={() => {
                      const sections = [...def.sections];
                      [sections[i], sections[i + 1]] = [sections[i + 1]!, sections[i]!];
                      update({ sections });
                    }}
                  >
                    <ArrowDown size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label="Remove section"
                    disabled={readOnly || def.sections.length <= 1}
                    onClick={() => update({ sections: def.sections.filter((_, j) => j !== i) })}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
              {!readOnly && unusedSections.length > 0 && (
                <div className="mt-1">
                  <Label>Add section</Label>
                  <Select
                    value=""
                    onChange={(e) => {
                      const key = e.target.value as SectionKey;
                      if (!key) return;
                      update({
                        sections: [...def.sections, { key, label: DEFAULT_SECTION_LABELS[key] }],
                      });
                    }}
                  >
                    <option value="">Choose…</option>
                    {unusedSections.map((k) => (
                      <option key={k} value={k}>
                        {DEFAULT_SECTION_LABELS[k]}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
              <p className="text-[12px] text-text-3">
                Sections without content are skipped in the output automatically.
              </p>
            </CardBody>
          </Card>

          <Card>
            <SectionHeader icon={Contact} title="Personal details rows" />
            <CardBody className="flex flex-col gap-2">
              {detailRows.map((f, i) => (
                <div key={f.key} className="flex items-center gap-2">
                  <span className="w-32 shrink-0 text-[12px] text-text-3">{f.key}</span>
                  <Input
                    value={f.label}
                    disabled={readOnly}
                    onChange={(e) => {
                      const personalDetails = [...detailRows];
                      personalDetails[i] = { ...f, label: e.target.value };
                      update({ personalDetails });
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label="Move up"
                    disabled={readOnly || i === 0}
                    onClick={() => {
                      const personalDetails = [...detailRows];
                      [personalDetails[i - 1], personalDetails[i]] = [
                        personalDetails[i]!,
                        personalDetails[i - 1]!,
                      ];
                      update({ personalDetails });
                    }}
                  >
                    <ArrowUp size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label="Move down"
                    disabled={readOnly || i === detailRows.length - 1}
                    onClick={() => {
                      const personalDetails = [...detailRows];
                      [personalDetails[i], personalDetails[i + 1]] = [
                        personalDetails[i + 1]!,
                        personalDetails[i]!,
                      ];
                      update({ personalDetails });
                    }}
                  >
                    <ArrowDown size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label="Remove row"
                    disabled={readOnly}
                    onClick={() =>
                      update({ personalDetails: detailRows.filter((_, j) => j !== i) })
                    }
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
              {!readOnly && unusedDetailKeys.length > 0 && (
                <div className="mt-1">
                  <Label>Add row</Label>
                  <Select
                    value=""
                    onChange={(e) => {
                      const key = e.target.value as PersonalDetailKey;
                      if (!key) return;
                      update({
                        personalDetails: [
                          ...detailRows,
                          { key, label: DEFAULT_PERSONAL_DETAIL_LABELS[key] },
                        ],
                      });
                    }}
                  >
                    <option value="">Choose…</option>
                    {unusedDetailKeys.map((k) => (
                      <option key={k} value={k}>
                        {DEFAULT_PERSONAL_DETAIL_LABELS[k]}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
              <p className="text-[12px] text-text-3">
                Rows whose value is empty are skipped in the output. Email and Telephone
                additionally need &ldquo;Contact details&rdquo; ticked on the review screen.
              </p>
            </CardBody>
          </Card>
            </>
          )}

          <Card>
            <SectionHeader icon={Building2} title="Branding" />
            <CardBody className="flex flex-col gap-3">
              <div>
                <Label>Company name (header fallback when no logo)</Label>
                <Input
                  value={def.branding.companyName}
                  disabled={readOnly}
                  onChange={(e) =>
                    update({ branding: { ...def.branding, companyName: e.target.value } })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {(["logo", "watermark"] as const).map((kind) => {
                  const url = kind === "logo" ? assetUrls.logo : assetUrls.watermark;
                  const sizeKey = kind === "logo" ? "logoSize" : "watermarkSize";
                  const size = def.branding[sizeKey];
                  return (
                    <div key={kind} className="flex flex-col gap-2">
                      <Label>{kind === "logo" ? "Logo" : "Watermark"} (PNG / JPG / WebP)</Label>
                      <div className="flex flex-col gap-2 rounded-md border border-dashed border-border-2 p-3">
                        {url ? (
                          <img
                            src={url}
                            alt={kind}
                            className="max-h-16 self-start rounded bg-white object-contain p-1"
                          />
                        ) : (
                          <p className="text-[12px] text-text-3">None uploaded — pending from AGP.</p>
                        )}
                        {!readOnly && (
                          <div className="flex items-center gap-3">
                            <label className="inline-flex cursor-pointer items-center gap-1.5 text-[13px] font-semibold text-brand-on">
                              <Upload size={14} /> Upload image
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) void uploadAsset(kind, file);
                                  e.target.value = "";
                                }}
                              />
                            </label>
                            {url && (
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 text-[13px] font-semibold text-danger-on"
                                onClick={() => removeAsset(kind)}
                              >
                                <Trash2 size={13} /> Remove
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {kind === "logo" ? (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label>Position</Label>
                            <Select
                              value={def.branding.logoAlign ?? "right"}
                              disabled={readOnly}
                              onChange={(e) =>
                                update({
                                  branding: {
                                    ...def.branding,
                                    logoAlign: e.target.value as "left" | "center" | "right",
                                  },
                                })
                              }
                            >
                              <option value="left">Top left</option>
                              <option value="center">Top center</option>
                              <option value="right">Top right</option>
                            </Select>
                          </div>
                          <div>
                            <Label>Layout</Label>
                            <Select
                              value={def.branding.logoLayout ?? "floating"}
                              disabled={readOnly}
                              onChange={(e) =>
                                update({
                                  branding: {
                                    ...def.branding,
                                    logoLayout: e.target.value as "floating" | "inline",
                                  },
                                })
                              }
                            >
                              <option value="floating">Floating — never pushes content</option>
                              <option value="inline">In line — takes header space</option>
                            </Select>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label>Horizontal</Label>
                            <Select
                              value={def.branding.watermarkHAlign ?? "center"}
                              disabled={readOnly}
                              onChange={(e) =>
                                update({
                                  branding: {
                                    ...def.branding,
                                    watermarkHAlign: e.target.value as "left" | "center" | "right",
                                  },
                                })
                              }
                            >
                              <option value="left">Left</option>
                              <option value="center">Center</option>
                              <option value="right">Right</option>
                            </Select>
                          </div>
                          <div>
                            <Label>Vertical</Label>
                            <Select
                              value={def.branding.watermarkVAlign ?? "center"}
                              disabled={readOnly}
                              onChange={(e) =>
                                update({
                                  branding: {
                                    ...def.branding,
                                    watermarkVAlign: e.target.value as "top" | "center" | "bottom",
                                  },
                                })
                              }
                            >
                              <option value="top">Top</option>
                              <option value="center">Middle</option>
                              <option value="bottom">Bottom</option>
                            </Select>
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <NumberField
                          label="Width"
                          suffix="px"
                          min={20}
                          max={800}
                          value={size.width}
                          onChange={(n) =>
                            update({
                              branding: { ...def.branding, [sizeKey]: { ...size, width: n } },
                            })
                          }
                        />
                        <NumberField
                          label="Height"
                          suffix="px"
                          min={20}
                          max={800}
                          value={size.height}
                          onChange={(n) =>
                            update({
                              branding: { ...def.branding, [sizeKey]: { ...size, height: n } },
                            })
                          }
                        />
                      </div>
                      <NumberField
                        label="Opacity"
                        suffix="%"
                        min={5}
                        max={100}
                        value={Math.round(
                          ((kind === "logo"
                            ? (def.branding.logoOpacity ?? 1)
                            : (def.branding.watermarkOpacity ?? 1)) as number) * 100,
                        )}
                        onChange={(n) =>
                          update({
                            branding: {
                              ...def.branding,
                              [kind === "logo" ? "logoOpacity" : "watermarkOpacity"]:
                                Math.min(100, Math.max(5, n)) / 100,
                            },
                          })
                        }
                      />
                    </div>
                  );
                })}
              </div>
              <div>
                <Label>Footer lines</Label>
                {readOnly ? (
                  <div className="text-[14px] text-text-2">
                    {def.branding.footerLines.map((l, i) => (
                      <div key={i}>{l}</div>
                    ))}
                  </div>
                ) : (
                  <StringListEditor
                    items={def.branding.footerLines}
                    onChange={(footerLines) =>
                      update({ branding: { ...def.branding, footerLines } })
                    }
                  />
                )}
              </div>
              <div>
                <Label>Default footer</Label>
                <Select
                  value={def.branding.footerPreset ?? ""}
                  disabled={readOnly}
                  onChange={(e) =>
                    update({
                      branding: { ...def.branding, footerPreset: e.target.value || undefined },
                    })
                  }
                >
                  <option value="">Footer lines above</option>
                  {footerPresets.map((p) => (
                    <option key={p.name} value={p.name}>
                      {p.name}
                    </option>
                  ))}
                  {def.branding.footerPreset &&
                    !footerPresets.some((p) => p.name === def.branding.footerPreset) && (
                      <option value={def.branding.footerPreset}>
                        {def.branding.footerPreset} (deleted — falls back to the lines above)
                      </option>
                    )}
                </Select>
                <p className="mt-1 text-[12px] text-text-3">
                  Footer texts are managed in Settings. The review screen can switch a resume to
                  another one.
                </p>
              </div>
              <div className="grid grid-cols-[1fr_auto_auto] items-end gap-3">
                <div>
                  <Label>Font</Label>
                  <Select
                    value={def.branding.footerStyle?.font ?? def.fonts.body}
                    disabled={readOnly}
                    onChange={(e) => patchFooterStyle({ font: e.target.value })}
                  >
                    {WORD_FONTS.map((f) => (
                      <option key={f}>{f}</option>
                    ))}
                  </Select>
                </div>
                <NumberField
                  label="Footer size"
                  suffix="pt"
                  min={6}
                  max={12}
                  step={0.5}
                  value={def.branding.footerStyle?.sizePt ?? 8}
                  onChange={(n) => patchFooterStyle({ sizePt: n })}
                />
                <div>
                  <Label>Color</Label>
                  <input
                    type="color"
                    value={`#${def.branding.footerStyle?.color ?? def.colors.subtle}`}
                    disabled={readOnly}
                    aria-label="Footer text color"
                    className="h-9 w-12 cursor-pointer rounded-md border border-border bg-surface"
                    onChange={(e) =>
                      patchFooterStyle({ color: e.target.value.slice(1).toUpperCase() })
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 items-end gap-3">
                <label className="flex h-9 items-center gap-2 text-[14px] text-text">
                  <input
                    type="checkbox"
                    checked={def.branding.footerStyle?.bold === true}
                    disabled={readOnly}
                    onChange={(e) => patchFooterStyle({ bold: e.target.checked })}
                  />
                  Bold
                </label>
                <label className="flex h-9 items-center gap-2 text-[14px] text-text">
                  <input
                    type="checkbox"
                    checked={def.branding.footerStyle?.showRule !== false}
                    disabled={readOnly}
                    onChange={(e) => patchFooterStyle({ showRule: e.target.checked })}
                  />
                  Rule line
                </label>
              </div>
              {def.branding.footerStyle?.showRule !== false && (
                <div className="grid grid-cols-4 items-end gap-3">
                  <NumberField
                    label="Rule thickness"
                    suffix="pt"
                    min={0.5}
                    max={4}
                    step={0.5}
                    value={def.branding.footerStyle?.ruleWidthPt ?? 1}
                    onChange={(n) => patchFooterStyle({ ruleWidthPt: n })}
                  />
                  <NumberField
                    label="Rule spacing"
                    suffix="pt"
                    min={0}
                    max={12}
                    value={def.branding.footerStyle?.ruleSpacingPt ?? 4}
                    onChange={(n) => patchFooterStyle({ ruleSpacingPt: n })}
                  />
                </div>
              )}
            </CardBody>
          </Card>
            </>
          )}
        </div>

        <div className="min-w-0 lg:sticky lg:top-4">
          <div className="rounded-lg bg-surface-2 p-3">
            {isJd ? (
              <JdPreview definition={def} logoUrl={assetUrls.logo} watermarkUrl={assetUrls.watermark} />
            ) : (
              <TemplatePreview
                definition={def}
                logoUrl={assetUrls.logo}
                watermarkUrl={assetUrls.watermark}
                photoPlaceholder
              />
            )}
          </div>
          <p className="mt-2 text-center text-[12px] text-text-3">
            Live preview with sample data — fonts are browser approximations; use “Sample DOCX”
            for the exact Word output.
          </p>
        </div>
      </div>
    </div>
  );
}
